import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as React from "react";
import { useState } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import { Button, CardLink, FormGroup, Input, Label, Spinner } from "reactstrap";
import { Dispatch } from "redux";
import { ratingAction } from "../../actions/ratingAction";
import { DABButton, LoadingSpinnerModal } from "../../components/dab";
import { showAlert } from "../common/functions";

const RATING_TITLE_MAX_LENGTH = 50;
const RATING_DESCRIPTION_MAX_LENGTH = 150;
const REMIND_LATER_BUTTON_MAX_LENGTH = 30;
const FEEDBACK_TITLE_MAX_LENGTH = 50;
const FEEDBACK_DESCRIPTION_MAX_LENGTH = 150;
const FEEDBACK_BUTTON_MAX_LENGTH = 30;
const STORE_URL_MAX_LENGTH = 500;
const WHITESPACE_ONLY_MESSAGE = "Input cannot be only spaces";

const setWhitespaceValidity = (
	element: HTMLTextAreaElement | HTMLInputElement | null,
	value: string
) => {
	if (!element) {
		return;
	}
	element.setCustomValidity(value && !value.trim() ? WHITESPACE_ONLY_MESSAGE : "");
};

interface RatingSettingProps {
	dispatch: Dispatch<any>;
	ratingData: any;
}

const RatingSettingDetail: React.SFC<RatingSettingProps> = ({
	dispatch,
	ratingData,
}) => {
	const history = useHistory();

	const [ratingEnabled, setRatingEnabled] = useState(false);
	const [ratingTitle, setRatingTitle] = useState("");
	const [ratingDescription, setRatingDescription] = useState("");
	const [remindLaterButtonText, setRemindLaterButtonText] = useState("");
	const [feedbackTitle, setFeedbackTitle] = useState("");
	const [feedbackDescription, setFeedbackDescription] = useState("");
	const [feedbackButtonText, setFeedbackButtonText] = useState("");
	const [appleStoreUrl, setAppleStoreUrl] = useState("");
	const [googlePlayUrl, setGooglePlayUrl] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [formSubmit, setFormSubmit] = useState(false);
	const [showModal, setShowModal] = useState(false);
	const [isDetailLoaded, setIsDetailLoaded] = useState<boolean>(false);

	const ratingTitleRef = React.useRef<HTMLInputElement>(null);
	const ratingDescriptionRef = React.useRef<HTMLTextAreaElement>(null);
	const remindLaterButtonRef = React.useRef<HTMLInputElement>(null);
	const feedbackTitleRef = React.useRef<HTMLInputElement>(null);
	const feedbackDescriptionRef = React.useRef<HTMLTextAreaElement>(null);
	const feedbackButtonRef = React.useRef<HTMLInputElement>(null);

	React.useEffect(() => {
		setShowModal(true);
		dispatch(ratingAction.ratingGetSetting());
	}, []);

	React.useEffect(() => {
		setWhitespaceValidity(ratingTitleRef.current, ratingTitle);
	}, [ratingTitle]);

	React.useEffect(() => {
		setWhitespaceValidity(ratingDescriptionRef.current, ratingDescription);
	}, [ratingDescription]);

	React.useEffect(() => {
		setWhitespaceValidity(remindLaterButtonRef.current, remindLaterButtonText);
	}, [remindLaterButtonText]);

	React.useEffect(() => {
		setWhitespaceValidity(feedbackTitleRef.current, feedbackTitle);
	}, [feedbackTitle]);

	React.useEffect(() => {
		setWhitespaceValidity(feedbackDescriptionRef.current, feedbackDescription);
	}, [feedbackDescription]);

	React.useEffect(() => {
		setWhitespaceValidity(feedbackButtonRef.current, feedbackButtonText);
	}, [feedbackButtonText]);

	React.useEffect(() => {
		if (!isDetailLoaded && ratingData?.isDetailComplete && ratingData?.settingInput) {
			const detail = ratingData.settingInput?.setting ?? ratingData.settingInput;
			setRatingEnabled(detail?.ratingEnabled ?? false);
			setRatingTitle(detail?.ratingTitle ?? "");
			setRatingDescription(detail?.ratingDescription ?? "");
			setRemindLaterButtonText(detail?.remindLaterButtonText ?? "");
			setFeedbackTitle(detail?.feedbackTitle ?? "");
			setFeedbackDescription(detail?.feedbackDescription ?? "");
			setFeedbackButtonText(detail?.feedbackButtonText ?? "");
			setAppleStoreUrl(detail?.appleStoreUrl ?? "");
			setGooglePlayUrl(detail?.googlePlayUrl ?? "");
			setIsDetailLoaded(true);
			setShowModal(false);
		}
	}, [ratingData?.isDetailComplete]);

	if (ratingData) {
		if (ratingData.isError && !ratingData.isSuccess) {
			if (showModal) {
				setShowModal(false);
			}
			if (isSubmitting) {
				setIsSubmitting(false);
			}
			if (formSubmit) {
				setFormSubmit(false);
			}
			ratingData.isError = false;
			if (ratingData.status === "Unauthorized") {
				sessionStorage.setItem("fromPath", window.location.pathname + window.location.search);
				history.push("/401");
			} else {
				showAlert(ratingData.status || "Unable to load the rating settings.", true);
			}
		}

		if (
			showModal &&
			formSubmit &&
			!ratingData.isFormSubmit &&
			ratingData.isSuccess
		) {
			setShowModal(false);
			setIsSubmitting(false);
			setFormSubmit(false);
			showAlert(ratingData.status, false);
		}
	}

	const validateUrls = () => {
		const urlPattern = /^https?:\/\/.+/;
		
		if (appleStoreUrl && !urlPattern.test(appleStoreUrl)) {
			showAlert("Apple Store URL must start with http:// or https://", true);
			return false;
		}
		
		if (googlePlayUrl && !urlPattern.test(googlePlayUrl)) {
			showAlert("Google Play URL must start with http:// or https://", true);
			return false;
		}
		
		return true;
	};

	const handleSubmit = (event) => {
		event.preventDefault();

		if (isSubmitting) {
			return;
		}

		if (!validateUrls()) {
			return;
		}

		const data = {
			setting: {
				ratingEnabled: ratingEnabled,
				ratingTitle: ratingTitle,
				ratingDescription: ratingDescription,
				remindLaterButtonText: remindLaterButtonText,
				feedbackTitle: feedbackTitle,
				feedbackDescription: feedbackDescription,
				feedbackButtonText: feedbackButtonText,
				appleStoreUrl: appleStoreUrl,
				googlePlayUrl: googlePlayUrl,
			},
		};

		setIsSubmitting(true);
		setShowModal(true);
		dispatch(ratingAction.ratingUpdateSetting(data));
		setFormSubmit(true);
	};

	const handleConfirmCancel = (e) => {
		e.preventDefault();
		e.stopPropagation();
		history.push("/");
	};

	return (
		<>
			<div
				className="rating-setting-detail mb-5"
				data-testid="ratingSettingPage"
			>
				{showModal && (
					<LoadingSpinnerModal isShowModal={showModal} modalChange={() => {}} />
				)}
				<div className="card-box m-0">
					<div className="card-box-link-back-div header-back">
						<FontAwesomeIcon icon={faChevronLeft} />
						<CardLink
							onClick={() => history.push("/")}
							className="card-box-link-back"
							data-testid="ratingSetting-backtolist"
						>
							Back to List
						</CardLink>
					</div>
					<form
						name="updateform"
						onSubmit={handleSubmit}
						className="create-form"
						data-testid="ratingSettingForm"
					>
						<div className="form-section" data-testid="ratingVisibilitySection">
							<div
								className="form-section-title"
								data-testid="ratingVisibilityTitle"
							>
								Rate DAB Visibility
							</div>
							<div className="form-section-body">
								<div className="row">
									<div className="col-md-3">
										<Label data-testid="ratingEnabled" className="d-block pt-0">
											Rate DAB Status
										</Label>
										<FormGroup switch>
											<Input
												type="switch"
												className="active-switch"
												id="ratingEnabled"
												name="ratingEnabled"
												data-testid="ratingEnabledInput"
												checked={ratingEnabled}
												onChange={() => {
													setRatingEnabled(!ratingEnabled);
												}}
											/>
											<Label
												check
												htmlFor="ratingEnabled"
												className="ms-2"
												data-testid="ratingEnabledStatus"
											>
												{ratingEnabled ? "Enabled" : "Disabled"}
											</Label>
										</FormGroup>
									</div>
								</div>
								<div className="row">
									<div className="col-12">
										<small className="text-muted d-block mt-1">
											The app rating feature can collect feedback from both web and native applications. When enabled, it will be visible in the application/web development tool.
										</small>
									</div>
								</div>
							</div>
						</div>

						<div className="form-section" data-testid="initialRatingPopupSection">
							<div
								className="form-section-title"
								data-testid="initialRatingPopupTitle"
							>
								Initial Rating Popup
							</div>
							<div className="form-section-body">
								<div>
									<Label
										htmlFor="ratingTitle"
										data-testid="ratingTitle"
										className="pt-0"
									>
										Rate Title <span className="text-danger">*</span>
									</Label>
									<FormGroup>
										<Input
											type="text"
											id="ratingTitle"
											name="ratingTitle"
											data-testid="ratingTitleInput"
											innerRef={ratingTitleRef}
											required
											onChange={(event) => {
												setRatingTitle(event.target.value);
											}}
											value={ratingTitle}
											pattern=".*\S.*"
											title={WHITESPACE_ONLY_MESSAGE}
											maxLength={RATING_TITLE_MAX_LENGTH}
										/>
									</FormGroup>
								</div>
								<div>
									<Label htmlFor="ratingDescription" data-testid="ratingDescription">
										Rate Body <span className="text-danger">*</span>
									</Label>
									<FormGroup className="mb-0">
										<Input
											type="textarea"
											id="ratingDescription"
											name="ratingDescription"
											data-testid="ratingDescriptionInput"
											innerRef={ratingDescriptionRef}
											required
											rows={4}
											value={ratingDescription}
											onChange={(e) => {
												setRatingDescription(e.target.value);
											}}
											title={WHITESPACE_ONLY_MESSAGE}
											maxLength={RATING_DESCRIPTION_MAX_LENGTH}
										/>
										<div
											className="text-end text-muted character-count"
											data-testid="ratingDescriptionCount"
										>
											{ratingDescription?.length ?? 0}/{RATING_DESCRIPTION_MAX_LENGTH}
										</div>
									</FormGroup>
								</div>
								<div>
									<Label
										htmlFor="remindLaterButtonText"
										data-testid="remindLaterButtonText"
										className="pt-0"
									>
										Button Label <span className="text-danger">*</span>
									</Label>
									<FormGroup>
										<Input
											type="text"
											id="remindLaterButtonText"
											name="remindLaterButtonText"
											data-testid="remindLaterButtonTextInput"
											innerRef={remindLaterButtonRef}
											required
											onChange={(event) => {
												setRemindLaterButtonText(event.target.value);
											}}
											value={remindLaterButtonText}
											pattern=".*\S.*"
											title={WHITESPACE_ONLY_MESSAGE}
											maxLength={REMIND_LATER_BUTTON_MAX_LENGTH}
										/>
									</FormGroup>
								</div>
							</div>
						</div>

						<div className="form-section" data-testid="feedbackPopupSection">
							<div
								className="form-section-title"
								data-testid="feedbackPopupTitle"
							>
								Feedback Popup (1-3 Stars)
							</div>
							<div className="form-section-body">
								<div>
									<Label
										htmlFor="feedbackTitle"
										data-testid="feedbackTitle"
										className="pt-0"
									>
										Modal Title <span className="text-danger">*</span>
									</Label>
									<FormGroup>
										<Input
											type="text"
											id="feedbackTitle"
											name="feedbackTitle"
											data-testid="feedbackTitleInput"
											innerRef={feedbackTitleRef}
											required
											onChange={(event) => {
												setFeedbackTitle(event.target.value);
											}}
											value={feedbackTitle}
											pattern=".*\S.*"
											title={WHITESPACE_ONLY_MESSAGE}
											maxLength={FEEDBACK_TITLE_MAX_LENGTH}
										/>
									</FormGroup>
								</div>
								<div>
									<Label htmlFor="feedbackDescription" data-testid="feedbackDescription">
										Modal Body <span className="text-danger">*</span>
									</Label>
									<FormGroup className="mb-0">
										<Input
											type="textarea"
											id="feedbackDescription"
											name="feedbackDescription"
											data-testid="feedbackDescriptionInput"
											innerRef={feedbackDescriptionRef}
											required
											rows={4}
											value={feedbackDescription}
											onChange={(e) => {
												setFeedbackDescription(e.target.value);
											}}
											title={WHITESPACE_ONLY_MESSAGE}
											maxLength={FEEDBACK_DESCRIPTION_MAX_LENGTH}
										/>
										<div
											className="text-end text-muted character-count"
											data-testid="feedbackDescriptionCount"
										>
											{feedbackDescription?.length ?? 0}/{FEEDBACK_DESCRIPTION_MAX_LENGTH}
										</div>
									</FormGroup>
								</div>
								<div>
									<Label
										htmlFor="feedbackButtonText"
										data-testid="feedbackButtonText"
										className="pt-0"
									>
										Button Label <span className="text-danger">*</span>
									</Label>
									<FormGroup>
										<Input
											type="text"
											id="feedbackButtonText"
											name="feedbackButtonText"
											data-testid="feedbackButtonTextInput"
											innerRef={feedbackButtonRef}
											required
											onChange={(event) => {
												setFeedbackButtonText(event.target.value);
											}}
											value={feedbackButtonText}
											pattern=".*\S.*"
											title={WHITESPACE_ONLY_MESSAGE}
											maxLength={FEEDBACK_BUTTON_MAX_LENGTH}
										/>
									</FormGroup>
								</div>
							</div>
						</div>

						<div className="form-section" data-testid="appStoreLinksSection">
							<div
								className="form-section-title"
								data-testid="appStoreLinksTitle"
							>
								App Store Links
							</div>
							<div className="form-section-body">
								<div className="row">
									<div className="col-md-6">
										<Label
											htmlFor="appleStoreUrl"
											data-testid="appleStoreUrl"
											className="pt-0"
										>
											Apple App Store Link <span className="text-danger">*</span>
										</Label>
										<FormGroup className="mb-0">
											<Input
												type="text"
												id="appleStoreUrl"
												name="appleStoreUrl"
												data-testid="appleStoreUrlInput"
												required
												value={appleStoreUrl}
												onChange={(e) => {
													setAppleStoreUrl(e.target.value);
												}}
												placeholder="https://apps.apple.com/app/daily-audio-bible"
												pattern="^https?://.+"
												title="Please enter a valid URL starting with http:// or https://"
												maxLength={STORE_URL_MAX_LENGTH}
											/>
											<small className="text-muted d-block mt-1">
												Must be a valid URL starting with http:// or https://
											</small>
										</FormGroup>
									</div>
									<div className="col-md-6">
										<Label
											htmlFor="googlePlayUrl"
											data-testid="googlePlayUrl"
											className="pt-0"
										>
											Google Play Store Link <span className="text-danger">*</span>
										</Label>
										<FormGroup className="mb-0">
											<Input
												type="text"
												id="googlePlayUrl"
												name="googlePlayUrl"
												data-testid="googlePlayUrlInput"
												required
												value={googlePlayUrl}
												onChange={(e) => {
													setGooglePlayUrl(e.target.value);
												}}
												placeholder="https://play.google.com/store/apps/details?id=com.dab"
												pattern="^https?://.+"
												title="Please enter a valid URL starting with http:// or https://"
												maxLength={STORE_URL_MAX_LENGTH}
											/>
											<small className="text-muted d-block mt-1">
												Must be a valid URL starting with http:// or https://
											</small>
										</FormGroup>
									</div>
								</div>
							</div>
						</div>

						<div className="row">
							<div className="col-12 d-flex justify-content-between align-items-center mt-2">
								<div data-testid="cancelButtonContainer">
									<DABButton
										id="cancel"
										className={`${isSubmitting ? "disabled" : ""}`}
										onClick={handleConfirmCancel}
										value="CANCEL"
									>
										CANCEL
									</DABButton>
								</div>
								<div
									className="d-flex align-items-center gap-2"
									data-testid="saveButtonContainer"
								>
									<Button
										type="submit"
										id="submit"
										color="primary"
										className=""
										data-testid="saveButton"
										disabled={isSubmitting}
									>
										{isSubmitting && <Spinner size="sm"></Spinner>} SAVE
									</Button>
								</div>
							</div>
						</div>
					</form>
				</div>
			</div>
		</>
	);
};

const mapStateToProps = (state: any) => {
	const { ratingData } = state;
	return {
		ratingData,
	};
};

export default connect(mapStateToProps)(RatingSettingDetail);